import React, { useState, useRef, Children } from 'react';
import { motion, useInView } from 'framer-motion';
import { CheckIcon, XIcon, ChevronDownIcon, ChevronUpIcon, ShieldCheckIcon, ZapIcon, LeafIcon, ClockIcon, BookOpenIcon, ShoppingBagIcon } from 'lucide-react';
// ─── Animation Variants ───────────────────────────────────────────────────────
const fadeUp = {
  hidden: {
    opacity: 0,
    y: 40
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.7,
      ease: 'easeOut'
    }
  }
};
const staggerContainer = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.12
    }
  }
};
const staggerItem = {
  hidden: {
    opacity: 0,
    y: 24
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.55,
      ease: 'easeOut'
    }
  }
};
// ─── Scroll Reveal Wrapper ────────────────────────────────────────────────────
function Reveal({
  children,
  className = '',
  delay = 0
}: {
  children: React.ReactNode;
  className?: string;
  delay?: number;
}) {
  const ref = useRef(null);
  const inView = useInView(ref, {
    once: true,
    margin: '-80px'
  });
  return <motion.div ref={ref} initial="hidden" animate={inView ? 'visible' : 'hidden'} variants={{
    hidden: {
      opacity: 0,
      y: 40
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.7,
        ease: 'easeOut',
        delay
      }
    }
  }} className={className} data-id="element-2">
      {children}
    </motion.div>;
}
function RevealList({
  children,
  className = ''
}: {
  children: React.ReactNode;
  className?: string;
}) {
  const ref = useRef(null);
  const inView = useInView(ref, {
    once: true,
    margin: '-60px'
  });
  return <motion.div ref={ref} initial="hidden" animate={inView ? 'visible' : 'hidden'} variants={staggerContainer} className={className} data-id="element-3">
      {children}
    </motion.div>;
}
function RevealItem({
  children,
  className = ''
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return <motion.div variants={staggerItem} className={className} data-id="element-4">
      {children}
    </motion.div>;
}
// ─── Botanical SVG Decorations ────────────────────────────────────────────────
function BotanicalLeft() {
  return <svg className="absolute left-0 top-0 w-48 md:w-64 opacity-20 pointer-events-none" viewBox="0 0 200 300" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-5">
      <path d="M20 280 Q40 200 80 160 Q120 120 100 60" stroke="#C9A84C" strokeWidth="2" fill="none" data-id="element-6" />
      <path d="M80 160 Q40 140 20 100" stroke="#C9A84C" strokeWidth="1.5" fill="none" data-id="element-7" />
      <path d="M80 160 Q110 150 130 110" stroke="#C9A84C" strokeWidth="1.5" fill="none" data-id="element-8" />
      <ellipse cx="20" cy="100" rx="18" ry="10" fill="#C9A84C" opacity="0.4" transform="rotate(-30 20 100)" data-id="element-9" />
      <ellipse cx="130" cy="110" rx="18" ry="10" fill="#C9A84C" opacity="0.4" transform="rotate(20 130 110)" data-id="element-10" />
      <path d="M100 60 Q70 40 60 10" stroke="#C9A84C" strokeWidth="1.5" fill="none" data-id="element-11" />
      <path d="M100 60 Q130 40 140 10" stroke="#C9A84C" strokeWidth="1.5" fill="none" data-id="element-12" />
      <ellipse cx="60" cy="10" rx="14" ry="8" fill="#C9A84C" opacity="0.35" transform="rotate(-20 60 10)" data-id="element-13" />
      <ellipse cx="140" cy="10" rx="14" ry="8" fill="#C9A84C" opacity="0.35" transform="rotate(20 140 10)" data-id="element-14" />
      <circle cx="20" cy="280" r="4" fill="#C9A84C" opacity="0.5" data-id="element-15" />
      <circle cx="30" cy="265" r="3" fill="#C9A84C" opacity="0.4" data-id="element-16" />
    </svg>;
}
function BotanicalRight() {
  return <svg className="absolute right-0 bottom-0 w-48 md:w-64 opacity-20 pointer-events-none" viewBox="0 0 200 300" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-17">
      <path d="M180 20 Q160 100 120 140 Q80 180 100 240" stroke="#C9A84C" strokeWidth="2" fill="none" data-id="element-18" />
      <path d="M120 140 Q160 160 180 200" stroke="#C9A84C" strokeWidth="1.5" fill="none" data-id="element-19" />
      <path d="M120 140 Q90 150 70 190" stroke="#C9A84C" strokeWidth="1.5" fill="none" data-id="element-20" />
      <ellipse cx="180" cy="200" rx="18" ry="10" fill="#C9A84C" opacity="0.4" transform="rotate(30 180 200)" data-id="element-21" />
      <ellipse cx="70" cy="190" rx="18" ry="10" fill="#C9A84C" opacity="0.4" transform="rotate(-20 70 190)" data-id="element-22" />
      <path d="M100 240 Q130 260 140 290" stroke="#C9A84C" strokeWidth="1.5" fill="none" data-id="element-23" />
      <path d="M100 240 Q70 260 60 290" stroke="#C9A84C" strokeWidth="1.5" fill="none" data-id="element-24" />
      <ellipse cx="140" cy="290" rx="14" ry="8" fill="#C9A84C" opacity="0.35" transform="rotate(20 140 290)" data-id="element-25" />
      <ellipse cx="60" cy="290" rx="14" ry="8" fill="#C9A84C" opacity="0.35" transform="rotate(-20 60 290)" data-id="element-26" />
    </svg>;
}
// ─── FAQ Accordion ────────────────────────────────────────────────────────────
type FaqItem = {
  q: string;
  a: string;
};
function FaqAccordion({
  items
}: {
  items: FaqItem[];
}) {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  return <div className="space-y-3" data-id="element-27">
      {items.map((item, i) => <div key={i} className="border border-[#E8D5B0] rounded-xl overflow-hidden" style={{
      backgroundColor: '#FFFDF7'
    }} data-id="element-28">
          <button onClick={() => setOpenIndex(openIndex === i ? null : i)} className="w-full flex items-center justify-between px-6 py-4 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-[#C9A84C]" aria-expanded={openIndex === i} data-id="element-29">
            <span className="font-lato font-700 text-[#2C1810] pr-4" style={{
          fontFamily: 'Lato, sans-serif',
          fontWeight: 700
        }} data-id="element-30">
              {item.q}
            </span>
            {openIndex === i ? <ChevronUpIcon className="w-5 h-5 flex-shrink-0" style={{
          color: '#C9A84C'
        }} data-id="element-31" /> : <ChevronDownIcon className="w-5 h-5 flex-shrink-0" style={{
          color: '#C9A84C'
        }} data-id="element-32" />}
          </button>
          <motion.div initial={false} animate={{
        height: openIndex === i ? 'auto' : 0,
        opacity: openIndex === i ? 1 : 0
      }} transition={{
        duration: 0.3,
        ease: 'easeInOut'
      }} style={{
        overflow: 'hidden'
      }} data-id="element-33">
            <div className="px-6 pb-5 text-[#5C3D2E] leading-relaxed" style={{
          fontFamily: 'Lato, sans-serif'
        }} data-id="element-34">
              {item.a}
            </div>
          </motion.div>
        </div>)}
    </div>;
}
// ─── Main Landing Page ────────────────────────────────────────────────────────
export function LandingPage() {
  const [legalModal, setLegalModal] = useState<ModalType>(null);
  const faqItems: FaqItem[] = [{
    q: '¿Tengo que cocinar platos complicados?',
    a: 'No, son alimentos simples que encuentras en cualquier mercado. El protocolo está diseñado para mujeres ocupadas que no tienen tiempo para recetas elaboradas.'
  }, {
    q: '¿Es seguro si tengo menopausia?',
    a: 'Es ideal, ya que ataca la raíz hormonal de la inflamación. El método de regulación HHA está especialmente diseñado para los cambios hormonales de la perimenopausia y menopausia.'
  }, {
    q: '¿Puedo seguir tomando café?',
    a: 'Sí, pero te enseñamos el momento exacto para que no te inflame. Un pequeño ajuste en el horario puede hacer una gran diferencia en tu cortisol matutino.'
  }, {
    q: '¿Es un pago único?',
    a: 'Sí, pagas una vez y tienes acceso de por vida al PDF. Sin suscripciones, sin cargos ocultos, sin sorpresas.'
  }, {
    q: '¿Cuánto tiempo tarda en verse el resultado?',
    a: 'La mayoría de las usuarias reportan una reducción visible de la distensión abdominal en los primeros 3 días. En 7 días, los resultados son notablemente más consistentes.'
  }];
  return <div className="w-full min-h-screen" style={{
    backgroundColor: '#FDF8F0',
    color: '#2C1810'
  }} data-id="element-35">
      {/* ── SECTION 1: HERO ─────────────────────────────────────────────────── */}
      <section className="relative w-full overflow-hidden py-16 md:py-24 px-4" style={{
      background: 'linear-gradient(160deg, #FDF8F0 0%, #F5E6C8 50%, #FDF8F0 100%)'
    }} data-id="element-36">
        <BotanicalLeft data-id="element-37" />
        <BotanicalRight data-id="element-38" />

        <div className="relative z-10 max-w-4xl mx-auto text-center" data-id="element-39">
          {/* Badge */}
          <Reveal data-id="element-40">
            <span className="inline-block px-4 py-1.5 rounded-full text-sm font-bold tracking-widest uppercase mb-6" style={{
            backgroundColor: '#1E3A1F',
            color: '#C9A84C',
            fontFamily: 'Lato, sans-serif',
            letterSpacing: '0.12em'
          }} data-id="element-41">
              ✦ Para Mujeres de 40+ ✦
            </span>
          </Reveal>

          {/* H1 */}
          <Reveal delay={0.1} data-id="element-42">
            <h1 className="text-3xl md:text-5xl lg:text-6xl font-black leading-tight mb-6" style={{
            fontFamily: 'Playfair Display, serif',
            color: '#1E3A1F',
            fontWeight: 900
          }} data-id="element-43">
              EL PROTOCOLO PARA{' '}
              <span style={{
              color: '#C9A84C'
            }} data-id="element-44">
                DESINFLAMAR TU ABDOMEN
              </span>
              , REGULAR TU CORTISOL Y{' '}
              <span style={{
              color: '#C4622D'
            }} data-id="element-45">
                RECUPERAR TU CONFIANZA 40+
              </span>
            </h1>
          </Reveal>

          {/* H2 */}
          <Reveal delay={0.2} data-id="element-46">
            <p className="text-lg md:text-xl leading-relaxed max-w-2xl mx-auto mb-10" style={{
            fontFamily: 'Lato, sans-serif',
            color: '#5C3D2E'
          }} data-id="element-47">
              La guía paso a paso para mujeres que han probado todo y siguen
              despertando con la ropa apretada debido al estrés y al cortisol.
            </p>
          </Reveal>

          {/* Product Image */}
          <Reveal delay={0.3} data-id="element-48">
            <div className="relative max-w-2xl mx-auto mb-10" data-id="element-49">
              <div className="absolute inset-0 rounded-2xl blur-2xl opacity-30" style={{
              background: 'radial-gradient(ellipse, #C9A84C 0%, transparent 70%)'
            }} data-id="element-50" />
              <img src="https://cdn.magicpatterns.com/uploads/4wxMhis6J1tE7nUjGVSmJN/heylloyd-image-1772370734402.png" alt="Protocolo Vientre Plano 40+ — vista en laptop y móvil" className="relative w-full rounded-2xl" style={{
              boxShadow: '0 24px 64px rgba(30, 58, 31, 0.18), 0 4px 16px rgba(201, 168, 76, 0.2)'
            }} data-id="element-51" />
            </div>
          </Reveal>

          {/* CTA Button */}
          <Reveal delay={0.4} data-id="element-52">
            <div className="flex flex-col items-center gap-3" data-id="element-53">
              <button className="cta-btn px-8 py-5 rounded-2xl text-lg md:text-xl font-black uppercase tracking-wide cursor-pointer border-0" style={{
              fontFamily: 'Lato, sans-serif',
              color: '#1E3A1F',
              boxShadow: '0 8px 32px rgba(201, 168, 76, 0.4)',
              letterSpacing: '0.05em'
            }} aria-label="Comprar el Protocolo Vientre Plano 40+ por $6.070 CLP" onClick={() => document.getElementById('oferta')?.scrollIntoView({
              behavior: 'smooth'
            })} data-id="element-54">
                ¡SÍ, QUIERO MI PROTOCOLO AHORA!
              </button>
              <p className="text-sm" style={{
              fontFamily: 'Lato, sans-serif',
              color: '#5C3D2E'
            }} data-id="element-55">
                Acceso instantáneo · Solo{' '}
                <span style={{
                textDecoration: 'line-through',
                color: '#9A7A6A',
                marginRight: '4px'
              }} data-id="element-56">
                  $16.150
                </span>
                <strong style={{
                color: '#1E3A1F'
              }} data-id="element-57">
                  $6.070 CLP
                </strong>{' '}
                · Pago único de por vida
              </p>
              <div className="flex items-center gap-2 text-xs" style={{
              color: '#7A5C4A',
              fontFamily: 'Lato, sans-serif'
            }} data-id="element-58">
                <ShieldCheckIcon className="w-4 h-4" style={{
                color: '#C9A84C'
              }} data-id="element-59" />
                <span data-id="element-60">Compra 100% segura · Acceso inmediato al PDF</span>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ── SECTION 2: OPENING HOOK ─────────────────────────────────────────── */}
      <section className="w-full py-16 md:py-20 px-4" style={{
      backgroundColor: '#F5E6C8'
    }} data-id="element-61">
        <Reveal data-id="element-62">
          <div className="max-w-3xl mx-auto text-center relative" data-id="element-63">
            <div className="text-8xl md:text-9xl leading-none mb-4 select-none" style={{
            fontFamily: 'Playfair Display, serif',
            color: '#C9A84C',
            opacity: 0.5,
            lineHeight: 0.8
          }} aria-hidden="true" data-id="element-64">
              "
            </div>
            <p className="text-xl md:text-2xl leading-relaxed italic" style={{
            fontFamily: 'Playfair Display, serif',
            color: '#2C1810',
            fontStyle: 'italic'
          }} data-id="element-65">
              Son las 7:30 de la mañana. Te paras frente al espejo, te pones tu
              pantalón favorito y, de repente, el golpe de realidad: no cierra.
              O si cierra, te aprieta tanto que te sientes incómoda antes de
              empezar el día.
            </p>
            <p className="text-xl md:text-2xl leading-relaxed italic mt-4" style={{
            fontFamily: 'Playfair Display, serif',
            color: '#2C1810',
            fontStyle: 'italic'
          }} data-id="element-66">
              No es que hayas ganado grasa de la noche a la mañana. Es esa
              inflamación abdominal{' '}
              <strong style={{
              fontStyle: 'normal',
              color: '#C4622D'
            }} data-id="element-67">
                tensa, dura y frustrante
              </strong>{' '}
              que parece aparecer de la nada y no se va con nada.
            </p>
            <div className="text-8xl md:text-9xl leading-none mt-2 select-none" style={{
            fontFamily: 'Playfair Display, serif',
            color: '#C9A84C',
            opacity: 0.5,
            lineHeight: 0.8
          }} aria-hidden="true" data-id="element-68">
              "
            </div>
          </div>
        </Reveal>
      </section>

      {/* ── SECTION 3: THE PROBLEM ──────────────────────────────────────────── */}
      <section className="w-full py-16 md:py-20 px-4" style={{
      backgroundColor: '#FDF8F0'
    }} data-id="element-69">
        <div className="max-w-3xl mx-auto" data-id="element-70">
          <Reveal data-id="element-71">
            <div className="text-center mb-10" data-id="element-72">
              <span className="text-xs font-black tracking-widest uppercase mb-3 block" style={{
              fontFamily: 'Lato, sans-serif',
              color: '#C9A84C',
              letterSpacing: '0.2em'
            }} data-id="element-73">
                El Problema
              </span>
              <div className="section-divider" data-id="element-74" />
              <h2 className="text-2xl md:text-4xl font-bold leading-tight" style={{
              fontFamily: 'Playfair Display, serif',
              color: '#1E3A1F'
            }} data-id="element-75">
                ¿Por qué tu cuerpo se convierte en una{' '}
                <em style={{
                color: '#C4622D'
              }} data-id="element-76">
                  "esponja de cortisol"
                </em>{' '}
                después de los 40?
              </h2>
            </div>
          </Reveal>

          <Reveal delay={0.1} data-id="element-77">
            <p className="text-lg leading-relaxed mb-6" style={{
            fontFamily: 'Lato, sans-serif',
            color: '#5C3D2E'
          }} data-id="element-78">
              A partir de los 40 años, las reglas del juego cambian. Tu cuerpo
              se vuelve una <strong data-id="element-79">"esponja de cortisol"</strong>. El estrés
              del trabajo, la familia y los cambios hormonales envían una señal
              de alerta a tu sistema nervioso.
            </p>
          </Reveal>

          <Reveal delay={0.15} data-id="element-80">
            <div className="rounded-2xl p-6 md:p-8 mb-6 border-l-4" style={{
            backgroundColor: '#F5E6C8',
            borderLeftColor: '#C4622D'
          }} data-id="element-81">
              <p className="text-lg leading-relaxed font-bold" style={{
              fontFamily: 'Lato, sans-serif',
              color: '#2C1810'
            }} data-id="element-82">
                ¿El resultado?
              </p>
              <p className="text-lg leading-relaxed mt-2" style={{
              fontFamily: 'Lato, sans-serif',
              color: '#5C3D2E'
            }} data-id="element-83">
                Tu digestión se detiene, tu cuerpo retiene líquidos y tu abdomen
                se inflama como un mecanismo de protección. Estás lidiando con
                una{' '}
                <strong style={{
                color: '#1E3A1F'
              }} data-id="element-84">
                  respuesta bioquímica
                </strong>
                , no con un problema de falta de voluntad.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ── SECTION 4: WHY IT DIDN'T WORK ──────────────────────────────────── */}
      <section className="w-full py-16 md:py-20 px-4" style={{
      backgroundColor: '#1E3A1F'
    }} data-id="element-85">
        <div className="max-w-3xl mx-auto" data-id="element-86">
          <Reveal data-id="element-87">
            <div className="text-center mb-10" data-id="element-88">
              <span className="text-xs font-black tracking-widest uppercase mb-3 block" style={{
              fontFamily: 'Lato, sans-serif',
              color: '#C9A84C',
              letterSpacing: '0.2em'
            }} data-id="element-89">
                Por Qué No Funcionó
              </span>
              <div className="section-divider" data-id="element-90" />
              <h2 className="text-2xl md:text-4xl font-bold leading-tight" style={{
              fontFamily: 'Playfair Display, serif',
              color: '#F5E6C8'
            }} data-id="element-91">
                Todo lo que intentaste tenía una razón para fallar
              </h2>
            </div>
          </Reveal>

          <RevealList className="space-y-4" data-id="element-92">
            <RevealItem data-id="element-93">
              <div className="flex items-start gap-4 rounded-xl p-5" style={{
              backgroundColor: 'rgba(245, 230, 200, 0.08)',
              border: '1px solid rgba(201, 168, 76, 0.2)'
            }} data-id="element-94">
                <div className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center mt-0.5" style={{
                backgroundColor: 'rgba(196, 98, 45, 0.2)'
              }} data-id="element-95">
                  <XIcon className="w-4 h-4" style={{
                  color: '#E8856A'
                }} data-id="element-96" />
                </div>
                <div data-id="element-97">
                  <p className="font-bold mb-1" style={{
                  fontFamily: 'Lato, sans-serif',
                  color: '#F5E6C8'
                }} data-id="element-98">
                    Las dietas de 1,200 calorías
                  </p>
                  <p style={{
                  fontFamily: 'Lato, sans-serif',
                  color: '#C8B89A'
                }} data-id="element-99">
                    Disparan tu cortisol, haciendo que tu cuerpo se aferre a la
                    inflamación por miedo a la hambruna.
                  </p>
                </div>
              </div>
            </RevealItem>

            <RevealItem data-id="element-100">
              <div className="flex items-start gap-4 rounded-xl p-5" style={{
              backgroundColor: 'rgba(245, 230, 200, 0.08)',
              border: '1px solid rgba(201, 168, 76, 0.2)'
            }} data-id="element-101">
                <div className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center mt-0.5" style={{
                backgroundColor: 'rgba(196, 98, 45, 0.2)'
              }} data-id="element-102">
                  <XIcon className="w-4 h-4" style={{
                  color: '#E8856A'
                }} data-id="element-103" />
                </div>
                <div data-id="element-104">
                  <p className="font-bold mb-1" style={{
                  fontFamily: 'Lato, sans-serif',
                  color: '#F5E6C8'
                }} data-id="element-105">
                    El cardio intenso de una hora
                  </p>
                  <p style={{
                  fontFamily: 'Lato, sans-serif',
                  color: '#C8B89A'
                }} data-id="element-106">
                    Inflama aún más tus tejidos y agota tus glándulas
                    suprarrenales, empeorando el ciclo de cortisol.
                  </p>
                </div>
              </div>
            </RevealItem>

            <RevealItem data-id="element-107">
              <div className="flex items-start gap-4 rounded-xl p-5" style={{
              backgroundColor: 'rgba(245, 230, 200, 0.08)',
              border: '1px solid rgba(201, 168, 76, 0.2)'
            }} data-id="element-108">
                <div className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center mt-0.5" style={{
                backgroundColor: 'rgba(196, 98, 45, 0.2)'
              }} data-id="element-109">
                  <XIcon className="w-4 h-4" style={{
                  color: '#E8856A'
                }} data-id="element-110" />
                </div>
                <div data-id="element-111">
                  <p className="font-bold mb-1" style={{
                  fontFamily: 'Lato, sans-serif',
                  color: '#F5E6C8'
                }} data-id="element-112">
                    Los batidos verdes "detox"
                  </p>
                  <p style={{
                  fontFamily: 'Lato, sans-serif',
                  color: '#C8B89A'
                }} data-id="element-113">
                    Suelen estar llenos de fibra cruda que tu sistema digestivo
                    lento no puede procesar, causando más gas y distensión.
                  </p>
                </div>
              </div>
            </RevealItem>

            <RevealItem data-id="element-114">
              <div className="flex items-start gap-4 rounded-xl p-5" style={{
              backgroundColor: 'rgba(245, 230, 200, 0.08)',
              border: '1px solid rgba(201, 168, 76, 0.2)'
            }} data-id="element-115">
                <div className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center mt-0.5" style={{
                backgroundColor: 'rgba(196, 98, 45, 0.2)'
              }} data-id="element-116">
                  <XIcon className="w-4 h-4" style={{
                  color: '#E8856A'
                }} data-id="element-117" />
                </div>
                <div data-id="element-118">
                  <p className="font-bold mb-1" style={{
                  fontFamily: 'Lato, sans-serif',
                  color: '#F5E6C8'
                }} data-id="element-119">
                    Ignorar el descanso
                  </p>
                  <p style={{
                  fontFamily: 'Lato, sans-serif',
                  color: '#C8B89A'
                }} data-id="element-120">
                    Hace que tu hormona del hambre (ghrelina) tome el control,
                    saboteando cualquier esfuerzo que hagas durante el día.
                  </p>
                </div>
              </div>
            </RevealItem>
          </RevealList>
        </div>
      </section>

      {/* ── SECTION 5: THE MECHANISM ────────────────────────────────────────── */}
      <section className="w-full py-16 md:py-20 px-4" style={{
      backgroundColor: '#FDF8F0'
    }} data-id="element-121">
        <div className="max-w-3xl mx-auto" data-id="element-122">
          <Reveal data-id="element-123">
            <div className="text-center mb-10" data-id="element-124">
              <span className="text-xs font-black tracking-widest uppercase mb-3 block" style={{
              fontFamily: 'Lato, sans-serif',
              color: '#C9A84C',
              letterSpacing: '0.2em'
            }} data-id="element-125">
                El Mecanismo Único
              </span>
              <div className="section-divider" data-id="element-126" />
              <h2 className="text-2xl md:text-4xl font-bold leading-tight" style={{
              fontFamily: 'Playfair Display, serif',
              color: '#1E3A1F'
            }} data-id="element-127">
                El Método de Regulación HHA
              </h2>
            </div>
          </Reveal>

          <Reveal delay={0.1} data-id="element-128">
            <div className="rounded-2xl p-8 md:p-10 text-center relative overflow-hidden" style={{
            background: 'linear-gradient(135deg, #F5E6C8 0%, #FDF8F0 100%)',
            border: '2px solid #C9A84C'
          }} data-id="element-129">
              <div className="absolute top-0 right-0 w-32 h-32 rounded-full opacity-10" style={{
              background: '#C9A84C',
              transform: 'translate(30%, -30%)'
            }} data-id="element-130" />
              <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6" style={{
              backgroundColor: '#1E3A1F'
            }} data-id="element-131">
                <ZapIcon className="w-8 h-8" style={{
                color: '#C9A84C'
              }} data-id="element-132" />
              </div>
              <p className="text-lg md:text-xl leading-relaxed" style={{
              fontFamily: 'Lato, sans-serif',
              color: '#2C1810'
            }} data-id="element-133">
                En lugar de atacar tu cuerpo con restricciones, este protocolo
                utiliza{' '}
                <strong style={{
                color: '#1E3A1F'
              }} data-id="element-134">
                  micro-pasos
                </strong>{' '}
                para "apagar" la señal de estrés de tu cerebro.
              </p>
              <p className="text-lg md:text-xl leading-relaxed mt-4" style={{
              fontFamily: 'Lato, sans-serif',
              color: '#5C3D2E'
            }} data-id="element-135">
                Al calmar el eje{' '}
                <strong style={{
                color: '#C4622D'
              }} data-id="element-136">
                  Hipotálamo-Hipofisario-Adrenal
                </strong>
                , tu cuerpo finalmente recibe el permiso para soltar el agua
                retenida y liberar los gases atrapados, devolviéndote la
                ligereza en tiempo récord.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ── SECTION 6: WHAT YOU GET ─────────────────────────────────────────── */}
      <section className="w-full py-16 md:py-20 px-4" style={{
      backgroundColor: '#F5E6C8'
    }} data-id="element-137">
        <div className="max-w-4xl mx-auto" data-id="element-138">
          <Reveal data-id="element-139">
            <div className="text-center mb-12" data-id="element-140">
              <span className="text-xs font-black tracking-widest uppercase mb-3 block" style={{
              fontFamily: 'Lato, sans-serif',
              color: '#C4622D',
              letterSpacing: '0.2em'
            }} data-id="element-141">
                Lo Que Recibes
              </span>
              <div className="section-divider" data-id="element-142" />
              <h2 className="text-2xl md:text-4xl font-bold leading-tight" style={{
              fontFamily: 'Playfair Display, serif',
              color: '#1E3A1F'
            }} data-id="element-143">
                Todo lo que incluye tu protocolo
              </h2>
            </div>
          </Reveal>

          <RevealList className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5" data-id="element-144">
            <RevealItem data-id="element-145">
              <div className="rounded-2xl p-6 h-full" style={{
              backgroundColor: '#FFFDF7',
              border: '1px solid #E8D5B0',
              boxShadow: '0 4px 20px rgba(30,58,31,0.06)'
            }} data-id="element-146">
                <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4" style={{
                backgroundColor: '#1E3A1F'
              }} data-id="element-147">
                  <ZapIcon className="w-6 h-6" style={{
                  color: '#C9A84C'
                }} data-id="element-148" />
                </div>
                <h3 className="text-lg font-bold mb-2" style={{
                fontFamily: 'Playfair Display, serif',
                color: '#1E3A1F'
              }} data-id="element-149">
                  Protocolo "Reset" de 72 horas
                </h3>
                <p style={{
                fontFamily: 'Lato, sans-serif',
                color: '#5C3D2E',
                fontSize: '0.95rem'
              }} data-id="element-150">
                  Para resultados inmediatos y visibles desde los primeros días.
                </p>
              </div>
            </RevealItem>

            <RevealItem data-id="element-151">
              <div className="rounded-2xl p-6 h-full" style={{
              backgroundColor: '#FFFDF7',
              border: '1px solid #E8D5B0',
              boxShadow: '0 4px 20px rgba(30,58,31,0.06)'
            }} data-id="element-152">
                <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4" style={{
                backgroundColor: '#1E3A1F'
              }} data-id="element-153">
                  <LeafIcon className="w-6 h-6" style={{
                  color: '#C9A84C'
                }} data-id="element-154" />
                </div>
                <h3 className="text-lg font-bold mb-2" style={{
                fontFamily: 'Playfair Display, serif',
                color: '#1E3A1F'
              }} data-id="element-155">
                  Mapa de Alimentos Anti-inflamatorios
                </h3>
                <p style={{
                fontFamily: 'Lato, sans-serif',
                color: '#5C3D2E',
                fontSize: '0.95rem'
              }} data-id="element-156">
                  Qué comer y qué pausar para desinflamar tu cuerpo desde
                  adentro.
                </p>
              </div>
            </RevealItem>

            <RevealItem data-id="element-157">
              <div className="rounded-2xl p-6 h-full" style={{
              backgroundColor: '#FFFDF7',
              border: '1px solid #E8D5B0',
              boxShadow: '0 4px 20px rgba(30,58,31,0.06)'
            }} data-id="element-158">
                <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4" style={{
                backgroundColor: '#1E3A1F'
              }} data-id="element-159">
                  <ClockIcon className="w-6 h-6" style={{
                  color: '#C9A84C'
                }} data-id="element-160" />
                </div>
                <h3 className="text-lg font-bold mb-2" style={{
                fontFamily: 'Playfair Display, serif',
                color: '#1E3A1F'
              }} data-id="element-161">
                  Técnica del Nervio Vago
                </h3>
                <p style={{
                fontFamily: 'Lato, sans-serif',
                color: '#5C3D2E',
                fontSize: '0.95rem'
              }} data-id="element-162">
                  Para bajar el cortisol en solo 2 minutos, en cualquier momento
                  del día.
                </p>
              </div>
            </RevealItem>

            <RevealItem data-id="element-163">
              <div className="rounded-2xl p-6 h-full" style={{
              backgroundColor: '#FFFDF7',
              border: '1px solid #E8D5B0',
              boxShadow: '0 4px 20px rgba(30,58,31,0.06)'
            }} data-id="element-164">
                <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4" style={{
                backgroundColor: '#1E3A1F'
              }} data-id="element-165">
                  <BookOpenIcon className="w-6 h-6" style={{
                  color: '#C9A84C'
                }} data-id="element-166" />
                </div>
                <h3 className="text-lg font-bold mb-2" style={{
                fontFamily: 'Playfair Display, serif',
                color: '#1E3A1F'
              }} data-id="element-167">
                  Rutina de Movimiento Inteligente
                </h3>
                <p style={{
                fontFamily: 'Lato, sans-serif',
                color: '#5C3D2E',
                fontSize: '0.95rem'
              }} data-id="element-168">
                  8 minutos de movimiento. Cero sudor, máximo flujo linfático y
                  drenaje.
                </p>
              </div>
            </RevealItem>

            <RevealItem data-id="element-169">
              <div className="rounded-2xl p-6 h-full" style={{
              backgroundColor: '#FFFDF7',
              border: '1px solid #E8D5B0',
              boxShadow: '0 4px 20px rgba(30,58,31,0.06)'
            }} data-id="element-170">
                <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4" style={{
                backgroundColor: '#1E3A1F'
              }} data-id="element-171">
                  <ShoppingBagIcon className="w-6 h-6" style={{
                  color: '#C9A84C'
                }} data-id="element-172" />
                </div>
                <h3 className="text-lg font-bold mb-2" style={{
                fontFamily: 'Playfair Display, serif',
                color: '#1E3A1F'
              }} data-id="element-173">
                  Guía de Compras Express
                </h3>
                <p style={{
                fontFamily: 'Lato, sans-serif',
                color: '#5C3D2E',
                fontSize: '0.95rem'
              }} data-id="element-174">
                  Para desinflamar tu despensa con productos simples de
                  cualquier mercado.
                </p>
              </div>
            </RevealItem>

            <RevealItem data-id="element-175">
              <div className="rounded-2xl p-6 h-full flex flex-col justify-center items-center text-center" style={{
              background: 'linear-gradient(135deg, #1E3A1F, #2D5016)',
              border: '2px solid #C9A84C',
              boxShadow: '0 4px 20px rgba(30,58,31,0.15)'
            }} data-id="element-176">
                <p className="text-4xl font-black mb-2" style={{
                fontFamily: 'Playfair Display, serif',
                color: '#C9A84C'
              }} data-id="element-177">
                  $6.070
                </p>
                <p className="text-sm font-bold uppercase tracking-wider mb-1" style={{
                fontFamily: 'Lato, sans-serif',
                color: '#F5E6C8'
              }} data-id="element-178">
                  Pago único
                </p>
                <p className="text-xs" style={{
                fontFamily: 'Lato, sans-serif',
                color: '#C8B89A'
              }} data-id="element-179">
                  Acceso de por vida al PDF completo
                </p>
              </div>
            </RevealItem>
          </RevealList>
        </div>
      </section>

      {/* ── SECTION 7: WHO IT'S FOR / NOT FOR ──────────────────────────────── */}
      <section className="w-full py-16 md:py-20 px-4" style={{
      backgroundColor: '#FDF8F0'
    }} data-id="element-180">
        <div className="max-w-4xl mx-auto" data-id="element-181">
          <Reveal data-id="element-182">
            <div className="text-center mb-12" data-id="element-183">
              <span className="text-xs font-black tracking-widest uppercase mb-3 block" style={{
              fontFamily: 'Lato, sans-serif',
              color: '#C9A84C',
              letterSpacing: '0.2em'
            }} data-id="element-184">
                ¿Es Para Ti?
              </span>
              <div className="section-divider" data-id="element-185" />
              <h2 className="text-2xl md:text-4xl font-bold" style={{
              fontFamily: 'Playfair Display, serif',
              color: '#1E3A1F'
            }} data-id="element-186">
                Este protocolo es para ti si...
              </h2>
            </div>
          </Reveal>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-187">
            {/* FOR */}
            <Reveal delay={0.1} data-id="element-188">
              <div className="rounded-2xl p-6 md:p-8" style={{
              backgroundColor: '#F0F7F0',
              border: '2px solid #2D5016'
            }} data-id="element-189">
                <h3 className="text-xl font-bold mb-5 flex items-center gap-2" style={{
                fontFamily: 'Playfair Display, serif',
                color: '#1E3A1F'
              }} data-id="element-190">
                  <CheckIcon className="w-6 h-6" style={{
                  color: '#2D5016'
                }} data-id="element-191" />
                  Sí es para ti
                </h3>
                <ul className="space-y-3" data-id="element-192">
                  <li className="flex items-start gap-3" data-id="element-193">
                    <CheckIcon className="w-5 h-5 flex-shrink-0 mt-0.5" style={{
                    color: '#2D5016'
                  }} data-id="element-194" />
                    <span style={{
                    fontFamily: 'Lato, sans-serif',
                    color: '#2C1810'
                  }} data-id="element-195">
                      Mujeres de 40+ que sienten que su cuerpo ya no responde a
                      lo que antes funcionaba.
                    </span>
                  </li>
                  <li className="flex items-start gap-3" data-id="element-196">
                    <CheckIcon className="w-5 h-5 flex-shrink-0 mt-0.5" style={{
                    color: '#2D5016'
                  }} data-id="element-197" />
                    <span style={{
                    fontFamily: 'Lato, sans-serif',
                    color: '#2C1810'
                  }} data-id="element-198">
                      Profesionales y madres que viven con altos niveles de
                      estrés diario.
                    </span>
                  </li>
                  <li className="flex items-start gap-3" data-id="element-199">
                    <CheckIcon className="w-5 h-5 flex-shrink-0 mt-0.5" style={{
                    color: '#2D5016'
                  }} data-id="element-200" />
                    <span style={{
                    fontFamily: 'Lato, sans-serif',
                    color: '#2C1810'
                  }} data-id="element-201">
                      Mujeres que sufren de pesadez abdominal y gases después de
                      comer.
                    </span>
                  </li>
                  <li className="flex items-start gap-3" data-id="element-202">
                    <CheckIcon className="w-5 h-5 flex-shrink-0 mt-0.5" style={{
                    color: '#2D5016'
                  }} data-id="element-203" />
                    <span style={{
                    fontFamily: 'Lato, sans-serif',
                    color: '#2C1810'
                  }} data-id="element-204">
                      Aquellas que buscan una solución práctica, rápida y basada
                      en la biología, no en el sufrimiento.
                    </span>
                  </li>
                </ul>
              </div>
            </Reveal>

            {/* NOT FOR */}
            <Reveal delay={0.2} data-id="element-205">
              <div className="rounded-2xl p-6 md:p-8" style={{
              backgroundColor: '#FDF5F2',
              border: '2px solid #E8C5B5'
            }} data-id="element-206">
                <h3 className="text-xl font-bold mb-5 flex items-center gap-2" style={{
                fontFamily: 'Playfair Display, serif',
                color: '#7A3020'
              }} data-id="element-207">
                  <XIcon className="w-6 h-6" style={{
                  color: '#C4622D'
                }} data-id="element-208" />
                  No es para ti si...
                </h3>
                <ul className="space-y-3" data-id="element-209">
                  <li className="flex items-start gap-3" data-id="element-210">
                    <XIcon className="w-5 h-5 flex-shrink-0 mt-0.5" style={{
                    color: '#C4622D'
                  }} data-id="element-211" />
                    <span style={{
                    fontFamily: 'Lato, sans-serif',
                    color: '#5C3D2E'
                  }} data-id="element-212">
                      Buscas una solución de pérdida de peso extrema en días.
                    </span>
                  </li>
                  <li className="flex items-start gap-3" data-id="element-213">
                    <XIcon className="w-5 h-5 flex-shrink-0 mt-0.5" style={{
                    color: '#C4622D'
                  }} data-id="element-214" />
                    <span style={{
                    fontFamily: 'Lato, sans-serif',
                    color: '#5C3D2E'
                  }} data-id="element-215">
                      Eres atleta de alto rendimiento sin problemas de
                      inflamación por estrés.
                    </span>
                  </li>
                  <li className="flex items-start gap-3" data-id="element-216">
                    <XIcon className="w-5 h-5 flex-shrink-0 mt-0.5" style={{
                    color: '#C4622D'
                  }} data-id="element-217" />
                    <span style={{
                    fontFamily: 'Lato, sans-serif',
                    color: '#5C3D2E'
                  }} data-id="element-218">
                      No estás dispuesta a cambiar tu café en ayunas por un
                      protocolo más saludable.
                    </span>
                  </li>
                </ul>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ── SECTION 8: HOW IT WORKS ─────────────────────────────────────────── */}
      <section className="w-full py-16 md:py-20 px-4" style={{
      backgroundColor: '#1E3A1F'
    }} data-id="element-219">
        <div className="max-w-3xl mx-auto" data-id="element-220">
          <Reveal data-id="element-221">
            <div className="text-center mb-12" data-id="element-222">
              <span className="text-xs font-black tracking-widest uppercase mb-3 block" style={{
              fontFamily: 'Lato, sans-serif',
              color: '#C9A84C',
              letterSpacing: '0.2em'
            }} data-id="element-223">
                Cómo Funciona
              </span>
              <div className="section-divider" data-id="element-224" />
              <h2 className="text-2xl md:text-4xl font-bold" style={{
              fontFamily: 'Playfair Display, serif',
              color: '#F5E6C8'
            }} data-id="element-225">
                Tres pasos hacia tu ligereza
              </h2>
            </div>
          </Reveal>

          <RevealList className="space-y-6" data-id="element-226">
            <RevealItem data-id="element-227">
              <div className="flex items-start gap-5" data-id="element-228">
                <div className="flex-shrink-0 w-12 h-12 rounded-full flex items-center justify-center text-xl font-black" style={{
                backgroundColor: '#C9A84C',
                color: '#1E3A1F',
                fontFamily: 'Playfair Display, serif'
              }} data-id="element-229">
                  1
                </div>
                <div className="pt-2" data-id="element-230">
                  <h3 className="text-xl font-bold mb-2" style={{
                  fontFamily: 'Playfair Display, serif',
                  color: '#F5E6C8'
                }} data-id="element-231">
                    Descarga e identifica
                  </h3>
                  <p style={{
                  fontFamily: 'Lato, sans-serif',
                  color: '#C8B89A'
                }} data-id="element-232">
                    Descargas el protocolo e identificas tus disparadores de
                    estrés personales con la guía incluida.
                  </p>
                </div>
              </div>
            </RevealItem>

            <RevealItem data-id="element-233">
              <div className="flex items-start gap-5" data-id="element-234">
                <div className="flex-shrink-0 w-12 h-12 rounded-full flex items-center justify-center text-xl font-black" style={{
                backgroundColor: '#C9A84C',
                color: '#1E3A1F',
                fontFamily: 'Playfair Display, serif'
              }} data-id="element-235">
                  2
                </div>
                <div className="pt-2" data-id="element-236">
                  <h3 className="text-xl font-bold mb-2" style={{
                  fontFamily: 'Playfair Display, serif',
                  color: '#F5E6C8'
                }} data-id="element-237">
                    Aplica la fase "Reset"
                  </h3>
                  <p style={{
                  fontFamily: 'Lato, sans-serif',
                  color: '#C8B89A'
                }} data-id="element-238">
                    Aplicas la fase Reset de 72 horas con las comidas y
                    respiraciones sugeridas. Sin complicaciones, sin hambre.
                  </p>
                </div>
              </div>
            </RevealItem>

            <RevealItem data-id="element-239">
              <div className="flex items-start gap-5" data-id="element-240">
                <div className="flex-shrink-0 w-12 h-12 rounded-full flex items-center justify-center text-xl font-black" style={{
                backgroundColor: '#C9A84C',
                color: '#1E3A1F',
                fontFamily: 'Playfair Display, serif'
              }} data-id="element-241">
                  3
                </div>
                <div className="pt-2" data-id="element-242">
                  <h3 className="text-xl font-bold mb-2" style={{
                  fontFamily: 'Playfair Display, serif',
                  color: '#F5E6C8'
                }} data-id="element-243">
                    Recupera y mantén
                  </h3>
                  <p style={{
                  fontFamily: 'Lato, sans-serif',
                  color: '#C8B89A'
                }} data-id="element-244">
                    Recuperas la comodidad en tu ropa y mantienes el resultado
                    con el escudo anti-rebote incluido en el protocolo.
                  </p>
                </div>
              </div>
            </RevealItem>
          </RevealList>
        </div>
      </section>

      {/* ── SECTION 9: PROOF ────────────────────────────────────────────────── */}
      <section className="w-full py-16 md:py-20 px-4" style={{
      backgroundColor: '#FDF8F0'
    }} data-id="element-245">
        <div className="max-w-3xl mx-auto text-center" data-id="element-246">
          <Reveal data-id="element-247">
            <span className="text-xs font-black tracking-widest uppercase mb-3 block" style={{
            fontFamily: 'Lato, sans-serif',
            color: '#C9A84C',
            letterSpacing: '0.2em'
          }} data-id="element-248">
              Resultados Reales
            </span>
            <div className="section-divider" data-id="element-249" />
            <div className="rounded-2xl p-8 md:p-12 relative overflow-hidden" style={{
            background: 'linear-gradient(135deg, #F5E6C8 0%, #FDF8F0 100%)',
            border: '2px solid #C9A84C'
          }} data-id="element-250">
              <div className="text-6xl mb-4 select-none" style={{
              fontFamily: 'Playfair Display, serif',
              color: '#C9A84C',
              opacity: 0.4
            }} aria-hidden="true" data-id="element-251">
                ★★★★★
              </div>
              <p className="text-xl md:text-2xl font-bold leading-relaxed mb-4" style={{
              fontFamily: 'Playfair Display, serif',
              color: '#1E3A1F'
            }} data-id="element-252">
                "En solo 7 días, la mayoría de las usuarias reportan una
                reducción visible de la distensión abdominal y, lo más
                importante, vuelven a abrocharse sus pantalones sin sentir que
                les falta el aire."
              </p>
              <p className="text-base" style={{
              fontFamily: 'Lato, sans-serif',
              color: '#5C3D2E'
            }} data-id="element-253">
                No es magia. Es{' '}
                <strong style={{
                color: '#1E3A1F'
              }} data-id="element-254">
                  regulación hormonal aplicada
                </strong>
                .
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ── SECTION 10: FAQ ─────────────────────────────────────────────────── */}
      <section className="w-full py-16 md:py-20 px-4" style={{
      backgroundColor: '#F5E6C8'
    }} data-id="element-255">
        <div className="max-w-2xl mx-auto" data-id="element-256">
          <Reveal data-id="element-257">
            <div className="text-center mb-10" data-id="element-258">
              <span className="text-xs font-black tracking-widest uppercase mb-3 block" style={{
              fontFamily: 'Lato, sans-serif',
              color: '#C4622D',
              letterSpacing: '0.2em'
            }} data-id="element-259">
                Preguntas Frecuentes
              </span>
              <div className="section-divider" data-id="element-260" />
              <h2 className="text-2xl md:text-4xl font-bold" style={{
              fontFamily: 'Playfair Display, serif',
              color: '#1E3A1F'
            }} data-id="element-261">
                Resolvemos tus dudas
              </h2>
            </div>
          </Reveal>
          <Reveal delay={0.1} data-id="element-262">
            <FaqAccordion items={faqItems} data-id="element-263" />
          </Reveal>
        </div>
      </section>

      {/* ── SECTION 11: OFFER + FINAL CTA ───────────────────────────────────── */}
      <section id="oferta" className="w-full py-20 md:py-28 px-4 relative overflow-hidden" style={{
      background: 'linear-gradient(160deg, #1E3A1F 0%, #2D5016 50%, #1E3A1F 100%)'
    }} data-id="element-264">
        {/* Decorative circles */}
        <div className="absolute top-0 left-0 w-96 h-96 rounded-full opacity-10 pointer-events-none" style={{
        background: '#C9A84C',
        transform: 'translate(-40%, -40%)'
      }} data-id="element-265" />
        <div className="absolute bottom-0 right-0 w-80 h-80 rounded-full opacity-10 pointer-events-none" style={{
        background: '#C9A84C',
        transform: 'translate(40%, 40%)'
      }} data-id="element-266" />

        <div className="relative z-10 max-w-2xl mx-auto text-center" data-id="element-267">
          <Reveal data-id="element-268">
            <span className="inline-block px-4 py-1.5 rounded-full text-sm font-bold tracking-widest uppercase mb-6" style={{
            backgroundColor: 'rgba(201, 168, 76, 0.15)',
            color: '#C9A84C',
            fontFamily: 'Lato, sans-serif',
            border: '1px solid rgba(201, 168, 76, 0.4)',
            letterSpacing: '0.12em'
          }} data-id="element-269">
              ✦ Oferta Especial ✦
            </span>
          </Reveal>

          <Reveal delay={0.1} data-id="element-270">
            <h2 className="text-3xl md:text-5xl font-black leading-tight mb-4" style={{
            fontFamily: 'Playfair Display, serif',
            color: '#F5E6C8'
          }} data-id="element-271">
              Recupera tu ligereza hoy mismo
            </h2>
          </Reveal>

          <Reveal delay={0.15} data-id="element-272">
            <p className="text-lg md:text-xl leading-relaxed mb-8" style={{
            fontFamily: 'Lato, sans-serif',
            color: '#C8B89A'
          }} data-id="element-273">
              Obtén el Protocolo Vientre Plano 40+ completo por un pago único de
              solo{' '}
              <strong style={{
              color: '#C9A84C'
            }} data-id="element-274">
                $6.070 CLP
              </strong>
              . Es menos de lo que cuesta una cena fuera, pero con resultados
              que verás en tu espejo cada mañana.
            </p>
          </Reveal>

          {/* Price display */}
          <Reveal delay={0.2} data-id="element-275">
            <div className="flex items-center justify-center gap-4 mb-8" data-id="element-276">
              <div data-id="element-277">
                <div className="flex items-center justify-center gap-3 mb-1" data-id="element-278">
                  <span className="text-3xl line-through opacity-50" style={{
                  fontFamily: 'Lato, sans-serif',
                  color: '#C8B89A',
                  textDecoration: 'line-through'
                }} data-id="element-279">
                    $16.150
                  </span>
                  <span className="text-xs font-bold uppercase tracking-widest px-2 py-1 rounded-full" style={{
                  backgroundColor: '#C4622D',
                  color: '#FDF8F0',
                  fontFamily: 'Lato, sans-serif'
                }} data-id="element-280">
                    63% OFF
                  </span>
                </div>
                <p className="text-7xl font-black" style={{
                fontFamily: 'Playfair Display, serif',
                color: '#C9A84C',
                lineHeight: 1
              }} data-id="element-281">
                  $6.070
                </p>
                <p className="text-sm mt-1" style={{
                fontFamily: 'Lato, sans-serif',
                color: '#C8B89A'
              }} data-id="element-282">
                  CLP · Pago único · Acceso de por vida
                </p>
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.25} data-id="element-283">
            <div className="flex flex-col items-center gap-4" data-id="element-284">
              <button className="cta-btn px-10 py-5 rounded-2xl text-lg md:text-xl font-black uppercase tracking-wide cursor-pointer border-0 w-full max-w-md" style={{
              fontFamily: 'Lato, sans-serif',
              color: '#1E3A1F',
              letterSpacing: '0.04em'
            }} aria-label="Comprar el Protocolo Vientre Plano 40+ ahora por $6.070 CLP" onClick={() => window.open('https://thesolutionkit.com//cart/50186440966501:1', '_blank')} data-id="element-285">
                ¡SÍ, QUIERO MI PROTOCOLO AHORA!
              </button>

              <p className="text-sm" style={{
              fontFamily: 'Lato, sans-serif',
              color: '#A89070'
            }} data-id="element-286">
                Acceso instantáneo tras la compra
              </p>

              <div className="flex flex-wrap justify-center gap-4 mt-2" data-id="element-287">
                <div className="flex items-center gap-2" data-id="element-288">
                  <ShieldCheckIcon className="w-4 h-4" style={{
                  color: '#C9A84C'
                }} data-id="element-289" />
                  <span className="text-xs" style={{
                  fontFamily: 'Lato, sans-serif',
                  color: '#A89070'
                }} data-id="element-290">
                    Compra segura
                  </span>
                </div>
                <div className="flex items-center gap-2" data-id="element-291">
                  <ZapIcon className="w-4 h-4" style={{
                  color: '#C9A84C'
                }} data-id="element-292" />
                  <span className="text-xs" style={{
                  fontFamily: 'Lato, sans-serif',
                  color: '#A89070'
                }} data-id="element-293">
                    Acceso inmediato al PDF
                  </span>
                </div>
                <div className="flex items-center gap-2" data-id="element-294">
                  <BookOpenIcon className="w-4 h-4" style={{
                  color: '#C9A84C'
                }} data-id="element-295" />
                  <span className="text-xs" style={{
                  fontFamily: 'Lato, sans-serif',
                  color: '#A89070'
                }} data-id="element-296">
                    Acceso de por vida
                  </span>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ── FOOTER ──────────────────────────────────────────────────────────── */}
      <footer className="w-full py-8 px-4 text-center" style={{
      backgroundColor: '#150F0A',
      borderTop: '1px solid #2C1810'
    }} data-id="element-297">
        <p className="text-sm" style={{
        fontFamily: 'Lato, sans-serif',
        color: '#7A5C4A'
      }} data-id="element-298">
          © 2025 Protocolo Vientre Plano 40+. Todos los derechos reservados.
        </p>
        <div className="flex items-center justify-center gap-4 mt-3" data-id="element-299">
          <button onClick={() => setLegalModal('privacy')} className="text-xs underline underline-offset-2 cursor-pointer border-0 bg-transparent transition-colors hover:opacity-80" style={{
          fontFamily: 'Lato, sans-serif',
          color: '#C9A84C'
        }} data-id="element-300">
            Política de Privacidad
          </button>
          <span style={{
          color: '#3A2010'
        }} data-id="element-301">
            ·
          </span>
          <button onClick={() => setLegalModal('terms')} className="text-xs underline underline-offset-2 cursor-pointer border-0 bg-transparent transition-colors hover:opacity-80" style={{
          fontFamily: 'Lato, sans-serif',
          color: '#C9A84C'
        }} data-id="element-302">
            Términos y Condiciones
          </button>
        </div>
        <p className="text-xs mt-3 max-w-xl mx-auto" style={{
        fontFamily: 'Lato, sans-serif',
        color: '#5C3D2E'
      }} data-id="element-303">
          Este producto es de carácter informativo y no reemplaza el consejo
          médico profesional. Consulta a tu médico antes de iniciar cualquier
          protocolo de salud.
        </p>
      </footer>

      {/* ── LEGAL MODALS ────────────────────────────────────────────────────── */}
      {legalModal && <LegalModal type={legalModal} onClose={() => setLegalModal(null)} data-id="element-304" />}
    </div>;
}
// ─── Legal Modal ──────────────────────────────────────────────────────────────
type ModalType = 'privacy' | 'terms' | null;
function LegalModal({
  type,
  onClose
}: {
  type: ModalType;
  onClose: () => void;
}) {
  if (!type) return null;
  const isPrivacy = type === 'privacy';
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} transition={{
    duration: 0.2
  }} className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{
    backgroundColor: 'rgba(30, 20, 10, 0.7)',
    backdropFilter: 'blur(4px)'
  }} onClick={onClose} data-id="element-305">
      <motion.div initial={{
      opacity: 0,
      y: 32,
      scale: 0.97
    }} animate={{
      opacity: 1,
      y: 0,
      scale: 1
    }} exit={{
      opacity: 0,
      y: 16,
      scale: 0.97
    }} transition={{
      duration: 0.3,
      ease: 'easeOut'
    }} className="relative w-full max-w-2xl max-h-[80vh] overflow-y-auto rounded-2xl p-8 md:p-10" style={{
      backgroundColor: '#FDF8F0',
      boxShadow: '0 32px 80px rgba(30,20,10,0.35)'
    }} onClick={e => e.stopPropagation()} data-id="element-306">
        {/* Close button */}
        <button onClick={onClose} className="absolute top-4 right-4 w-9 h-9 rounded-full flex items-center justify-center cursor-pointer border-0 transition-colors" style={{
        backgroundColor: '#F5E6C8',
        color: '#2C1810'
      }} aria-label="Cerrar" data-id="element-307">
          <XIcon className="w-4 h-4" data-id="element-308" />
        </button>

        {/* Header */}
        <div className="mb-6 pb-4" style={{
        borderBottom: '2px solid #F5E6C8'
      }} data-id="element-309">
          <span className="text-xs font-black tracking-widest uppercase block mb-2" style={{
          fontFamily: 'Lato, sans-serif',
          color: '#C9A84C',
          letterSpacing: '0.2em'
        }} data-id="element-310">
            {isPrivacy ? 'Legal' : 'Legal'}
          </span>
          <h2 className="text-2xl md:text-3xl font-bold" style={{
          fontFamily: 'Playfair Display, serif',
          color: '#1E3A1F'
        }} data-id="element-311">
            {isPrivacy ? 'Política de Privacidad' : 'Términos y Condiciones'}
          </h2>
        </div>

        {/* Content */}
        <div className="space-y-5 text-sm leading-relaxed" style={{
        fontFamily: 'Lato, sans-serif',
        color: '#5C3D2E'
      }} data-id="element-312">
          {isPrivacy ? <>
              <p data-id="element-313">
                <strong style={{
              color: '#1E3A1F'
            }} data-id="element-314">
                  Última actualización:
                </strong>{' '}
                Marzo 2025
              </p>
              <div data-id="element-315">
                <h3 className="font-bold text-base mb-2" style={{
              color: '#1E3A1F',
              fontFamily: 'Playfair Display, serif'
            }} data-id="element-316">
                  1. Información que recopilamos
                </h3>
                <p data-id="element-317">
                  Al realizar tu compra, recopilamos únicamente la información
                  necesaria para procesar tu pedido y entregarte el producto
                  digital: nombre y correo electrónico. No almacenamos datos de
                  tarjetas de crédito ni información bancaria.
                </p>
              </div>
              <div data-id="element-318">
                <h3 className="font-bold text-base mb-2" style={{
              color: '#1E3A1F',
              fontFamily: 'Playfair Display, serif'
            }} data-id="element-319">
                  2. Uso de tu información
                </h3>
                <p data-id="element-320">
                  Tu información se utiliza exclusivamente para: (a) entregar el
                  producto digital adquirido, (b) enviarte comunicaciones
                  relacionadas con tu compra, y (c) mejorar nuestros productos y
                  servicios. No vendemos, alquilamos ni compartimos tu
                  información personal con terceros.
                </p>
              </div>
              <div data-id="element-321">
                <h3 className="font-bold text-base mb-2" style={{
              color: '#1E3A1F',
              fontFamily: 'Playfair Display, serif'
            }} data-id="element-322">
                  3. Cookies
                </h3>
                <p data-id="element-323">
                  Esta página puede utilizar cookies técnicas necesarias para su
                  funcionamiento. No utilizamos cookies de seguimiento
                  publicitario sin tu consentimiento.
                </p>
              </div>
              <div data-id="element-324">
                <h3 className="font-bold text-base mb-2" style={{
              color: '#1E3A1F',
              fontFamily: 'Playfair Display, serif'
            }} data-id="element-325">
                  4. Seguridad
                </h3>
                <p data-id="element-326">
                  Implementamos medidas de seguridad estándar de la industria
                  para proteger tu información personal. Todas las transacciones
                  se procesan a través de plataformas de pago seguras y
                  certificadas.
                </p>
              </div>
              <div data-id="element-327">
                <h3 className="font-bold text-base mb-2" style={{
              color: '#1E3A1F',
              fontFamily: 'Playfair Display, serif'
            }} data-id="element-328">
                  5. Tus derechos
                </h3>
                <p data-id="element-329">
                  Tienes derecho a acceder, rectificar o eliminar tus datos
                  personales en cualquier momento. Para ejercer estos derechos,
                  puedes solicitarlo a través de los canales de atención al
                  cliente disponibles en tu plataforma de compra.
                </p>
              </div>
              <div data-id="element-330">
                <h3 className="font-bold text-base mb-2" style={{
              color: '#1E3A1F',
              fontFamily: 'Playfair Display, serif'
            }} data-id="element-331">
                  6. Cambios a esta política
                </h3>
                <p data-id="element-332">
                  Nos reservamos el derecho de actualizar esta política de
                  privacidad en cualquier momento. Los cambios serán publicados
                  en esta página con la fecha de actualización correspondiente.
                </p>
              </div>
            </> : <>
              <p data-id="element-333">
                <strong style={{
              color: '#1E3A1F'
            }} data-id="element-334">
                  Última actualización:
                </strong>{' '}
                Marzo 2025
              </p>
              <div data-id="element-335">
                <h3 className="font-bold text-base mb-2" style={{
              color: '#1E3A1F',
              fontFamily: 'Playfair Display, serif'
            }} data-id="element-336">
                  1. Aceptación de los términos
                </h3>
                <p data-id="element-337">
                  Al adquirir el Protocolo Vientre Plano 40+, aceptas estos
                  Términos y Condiciones en su totalidad. Si no estás de acuerdo
                  con alguno de ellos, te pedimos que no realices la compra.
                </p>
              </div>
              <div data-id="element-338">
                <h3 className="font-bold text-base mb-2" style={{
              color: '#1E3A1F',
              fontFamily: 'Playfair Display, serif'
            }} data-id="element-339">
                  2. Naturaleza del producto
                </h3>
                <p data-id="element-340">
                  El Protocolo Vientre Plano 40+ es un producto digital de
                  carácter informativo y educativo. No constituye consejo
                  médico, diagnóstico ni tratamiento. Siempre consulta a un
                  profesional de la salud antes de iniciar cualquier cambio en
                  tu alimentación o rutina.
                </p>
              </div>
              <div data-id="element-341">
                <h3 className="font-bold text-base mb-2" style={{
              color: '#1E3A1F',
              fontFamily: 'Playfair Display, serif'
            }} data-id="element-342">
                  3. Entrega del producto
                </h3>
                <p data-id="element-343">
                  Tras confirmar tu pago, recibirás acceso inmediato al PDF
                  digital. El acceso es de por vida y para uso personal
                  exclusivo. Queda prohibida la reproducción, distribución o
                  reventa del contenido.
                </p>
              </div>
              <div data-id="element-344">
                <h3 className="font-bold text-base mb-2" style={{
              color: '#1E3A1F',
              fontFamily: 'Playfair Display, serif'
            }} data-id="element-345">
                  4. Política de reembolso
                </h3>
                <p data-id="element-346">
                  Dado que se trata de un producto digital con acceso inmediato,
                  no se ofrecen reembolsos una vez que el producto ha sido
                  descargado o accedido. Si tienes algún problema técnico con tu
                  compra, comunícate a través de la plataforma de pago
                  utilizada.
                </p>
              </div>
              <div data-id="element-347">
                <h3 className="font-bold text-base mb-2" style={{
              color: '#1E3A1F',
              fontFamily: 'Playfair Display, serif'
            }} data-id="element-348">
                  5. Resultados
                </h3>
                <p data-id="element-349">
                  Los resultados mencionados en esta página son representativos
                  y pueden variar según cada persona. No garantizamos resultados
                  específicos. El éxito del protocolo depende de la constancia y
                  las condiciones individuales de cada usuaria.
                </p>
              </div>
              <div data-id="element-350">
                <h3 className="font-bold text-base mb-2" style={{
              color: '#1E3A1F',
              fontFamily: 'Playfair Display, serif'
            }} data-id="element-351">
                  6. Propiedad intelectual
                </h3>
                <p data-id="element-352">
                  Todo el contenido del protocolo, incluyendo textos, gráficos y
                  metodología, es propiedad exclusiva de sus autores y está
                  protegido por las leyes de propiedad intelectual aplicables.
                </p>
              </div>
              <div data-id="element-353">
                <h3 className="font-bold text-base mb-2" style={{
              color: '#1E3A1F',
              fontFamily: 'Playfair Display, serif'
            }} data-id="element-354">
                  7. Ley aplicable
                </h3>
                <p data-id="element-355">
                  Estos términos se rigen por las leyes aplicables en la
                  jurisdicción del vendedor. Cualquier disputa será resuelta de
                  buena fe entre las partes.
                </p>
              </div>
            </>}
        </div>
      </motion.div>
    </motion.div>;
}